/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { type AsyncHasMany, attr, belongsTo, hasMany, type SyncHasMany } from '@ember-data/model';
import { type ContentState, State } from 'embercom/models/data/external-contents/constants';
import { EntityType } from './data/entity-types';
import type ContentFragment from './content-service/content-fragment';
import { State as OutboundState } from 'embercom/models/data/matching-system/matching-constants';
import { AiContentState, FinAvailability } from 'embercom/lib/ai-content-library/constants';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { MonolingualKnowledgeContentModel } from 'embercom/objects/knowledge-hub/knowledge-hub-content';
import type Folder from 'embercom/models/content-service/folder';
import type FolderMembership from 'embercom/models/content-service/folder-membership';
import { type LocalizedKnowledgeContentVersion } from 'embercom/objects/knowledge-hub/localized-knowledge-content-version';
import { type Taggable } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type Tag from 'embercom/models/tag';
import type Admin from 'embercom/models/admin';
import { UPDATE_TAGGING_URL } from 'embercom/lib/knowledge-hub/constants';
import { put } from 'embercom/lib/ajax';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import type ContentReviewRequest from './content-service/content-review-request';

export default class ExternalContent
  extends MonolingualKnowledgeContentModel
  implements LocalizedKnowledgeContentVersion
{
  @service declare appService: AppService;
  @service declare notificationsService: any;

  @attr('string') declare appId: string;
  @attr('date') declare lastIngestedAt: string;
  @attr('string') declare markdown: string;
  @attr('string') declare markdownUnfiltered: string;
  @attr('date') declare nextAutoSync: string;
  @attr('number') declare state: State;
  @attr('number') declare contentState: ContentState;
  @attr('string') declare title: string;
  @attr('string') declare url: string;
  @attr('number') declare sourceType: EntityType;
  @attr('number') declare sourceId: number;
  @attr('date') declare updatedAt: string;
  @attr('date') declare createdAt: string;
  @attr('number') declare finState: AiContentState;
  @attr('number') declare copilotAvailability: FinAvailability;
  @attr('number') declare chatbotAvailability: FinAvailability;
  @attr('number') declare salesAgentAvailability: FinAvailability;
  @attr('number') declare copilotState: FinAvailability;
  @attr('boolean') declare isCopilotStateEditable: boolean;
  @attr('string') declare languageCode: string;
  @attr('array') declare aiContentSegmentIds: Array<number> | [];
  @belongsTo('content-service/folder', { async: true }) declare folder?: Folder;
  @belongsTo('content-service/folder-membership', { async: false })
  declare folderMembership?: FolderMembership;
  @hasMany('tag', { async: true }) declare tags: AsyncHasMany<Tag>;
  @hasMany('tagging', { async: false }) declare taggings: SyncHasMany<any>;
  @hasMany('content-service/content-review-request', { async: false })
  declare contentReviewRequests: ContentReviewRequest[];

  @service declare intl: IntlService & { languageNameFromCode: (code: string) => string };

  get entityType(): EntityType.ExternalContent {
    return EntityType.ExternalContent;
  }

  get isPublished(): boolean {
    return this.finState === AiContentState.USED_BY_FIN;
  }

  async toggleState() {
    this.state = this.isPublished ? State.unpublished : State.published;
    await this.save();

    if (this.associatedListItemFragment) {
      if (this.isPublished) {
        this.associatedListItemFragment.state = OutboundState.Live;
      } else {
        this.associatedListItemFragment.state = OutboundState.Paused;
      }
    }
  }

  async updateLanguageCode(locale: string) {
    this.languageCode = locale;
    await taskFor(this.saveExternalContent).perform(
      this.intl.t('ai-content-library.external-content.language-update.success'),
      this.intl.t('ai-content-library.external-content.language-update.error'),
    );

    if (this.associatedListItemFragment) {
      this.associatedListItemFragment.locale = locale;
    }
  }

  @task *saveExternalContent(successMessage: string, failureMessage: string): TaskGenerator<void> {
    try {
      yield this.save();

      this.notificationsService.notifyConfirmation(successMessage);
    } catch (e) {
      this.notificationsService.notifyResponseError(e, {
        default: failureMessage,
      });
    }
  }

  get language(): string | void {
    if (this.languageCode) {
      return this.intl.languageNameFromCode(this.languageCode);
    }
  }

  get associatedListItemFragment(): ContentFragment | undefined {
    return this.store.peekRecord(
      'content-service/content-fragment',
      `${EntityType.ExternalContent}_${this.id}`,
    );
  }

  // start: KnowledgeHubContent / KnowledgeHubItemInterface

  get editorConfig() {
    let viewSidePanelConfig = [
      { name: 'knowledge-hub/content-editor/external-content/data-section' },
    ];

    viewSidePanelConfig.push({
      name: 'knowledge-hub/content-editor/shared/fin-section',
    });

    viewSidePanelConfig.push(
      { name: 'knowledge-hub/content-editor/external-content/link-section' },
      { name: 'knowledge-hub/content-editor/shared/reporting-section' },
      { name: 'knowledge-hub/content-editor/shared/tags-section' },
      ...(!this.appService.app.isStandaloneApp
        ? [{ name: 'knowledge-hub/content-editor/folder-section' }]
        : []),
    );

    let editSidePanelConfig = [{ name: 'knowledge-hub/content-editor/shared/fin-section' }];

    editSidePanelConfig.push(
      { name: 'knowledge-hub/content-editor/shared/tags-section' },
      ...(!this.appService.app.isStandaloneApp
        ? [{ name: 'knowledge-hub/content-editor/folder-section' }]
        : []),
    );

    return {
      viewMode: {
        headerConfig: { name: 'knowledge-hub/content-editor/external-content/view/header' },
        editorConfig: { name: 'knowledge-hub/content-editor/external-content/view/editor' },
        sidePanelConfig: viewSidePanelConfig,
      },
      editMode: {
        headerConfig: { name: '' },
        editorConfig: { name: '' },
        sidePanelConfig: editSidePanelConfig,
      },
    };
  }

  get chatbotAvailabilityOption(): FinAvailability {
    return this.chatbotAvailability;
  }

  set chatbotAvailabilityOption(availability: FinAvailability) {
    this.chatbotAvailability = availability;
  }

  get copilotAvailabilityOption(): FinAvailability {
    return this.copilotAvailability;
  }

  set copilotAvailabilityOption(availability: FinAvailability) {
    this.copilotAvailability = availability;
  }

  get salesAgentAvailabilityOption(): FinAvailability {
    return this.salesAgentAvailability;
  }

  set salesAgentAvailabilityOption(availability: FinAvailability) {
    this.salesAgentAvailability = availability;
  }

  get parentIdFromRelationship(): string | undefined {
    // @ts-ignore - TS doesn't like the 'folder' key here for some reason
    let folderRelationship = this.belongsTo('folder');
    return folderRelationship?.id();
  }

  get parent() {
    return this.folder;
  }

  set parent(parent: Folder | undefined) {
    this.folder = parent;
    this.onParentChange(parent);
  }

  get displayName(): string {
    return (
      this.title ||
      this.intl.t('ai-content-library.list.cells.title.untitled', {
        objectName: this.humanReadableContentName,
      })
    );
  }

  get isEditable() {
    return false;
  }

  get copilotAvailableValue(): FinAvailability {
    return FinAvailability.DERIVED;
  }

  get chatbotAvailableValue(): FinAvailability {
    return FinAvailability.DERIVED;
  }

  get salesAgentAvailableValue(): FinAvailability {
    return FinAvailability.DERIVED;
  }

  async isEligibleForCopilot() {
    return this.isCopilotStateEditable && this.localeIsEligibleForAi;
  }

  async isEligibleForChatbot() {
    return this.isCopilotStateEditable && this.localeIsEligibleForAi;
  }

  async isEligibleForSalesAgent() {
    return this.isCopilotStateEditable && this.localeIsEligibleForAi;
  }

  get latestVersion() {
    return this;
  }

  get localization() {
    return this;
  }

  get versionTitle(): string | undefined {
    return this.title;
  }

  set versionTitle(_value: string | undefined) {
    throw new Error('Webpage does not support editing');
  }

  get locale() {
    return this.languageCode;
  }

  set locale(value: string) {
    this.languageCode = value;
  }

  get taggable(): Taggable | undefined {
    return {
      tags: this.tags,
      taggings: this.taggings,
      type: 'external-content',
      updateTaggings: async (
        _admin: Admin,
        addedTags: Tag[],
        removedTags: Tag[],
        _initialTags: Tag[],
      ) => {
        return await this.updateTaggings(addedTags, removedTags);
      },
    };
  }

  // end: KnowledgeHubContent / KnowledgeHubItemInterface

  async updateTaggings(addedTags: Tag[], removedTags: Tag[]) {
    await put(UPDATE_TAGGING_URL, {
      app_id: this.app.id,
      added_tag_ids: addedTags.map((tag) => tag.id),
      removed_tag_ids: removedTags.map((tag) => tag.id),
      content_wrapper_id: this.id,
      content_wrapper_type: ContentWrapperType.SUPPORT_CONTENT,
      entity_type: EntityType.ExternalContent,
      entity_id: this.id,
    });
  }
}
